import { BillingInterval, StripeProduct, StripeProducts } from "../types"
import { findPriceByInterval, getRegionalPrice } from "./regional-pricing"

/**
 * Checks if there are both monthly and yearly pricing options available
 * @param stripeProducts - The stripe products object
 * @returns boolean indicating if multiple billing intervals are available
 */
export function hasMultipleBillingIntervals(
  stripeProducts: StripeProducts,
): boolean {
  if (Object.keys(stripeProducts).length === 0) return false

  let hasMonthly = false
  let hasYearly = false

  for (const product of Object.values(stripeProducts)) {
    if (!product.prices) continue

    for (const price of product.prices) {
      if (!price.recurring || price.recurring.interval_count !== 1) continue

      const interval = price.recurring.interval
      if (interval === BillingInterval.MONTH) hasMonthly = true
      if (interval === BillingInterval.YEAR) hasYearly = true

      if (hasMonthly && hasYearly) return true
    }
  }

  return false
}

/**
 * Filters and sorts products based on the selected billing interval
 * @param stripeProducts - The stripe products object
 * @param selectedInterval - The selected billing interval ("month" | "year")
 * @param userSubscriptionPlan - Optional user subscription plan to match enterprise plans
 * @returns Array of filtered and sorted StripeProduct objects
 */
export function getFilteredAndSortedProducts(
  stripeProducts: StripeProducts,
  selectedInterval: BillingInterval,
  userSubscriptionPlan?: string,
): StripeProduct[] {
  if (Object.keys(stripeProducts).length === 0) return []

  const products = Object.values(stripeProducts)
    .filter((product) => {
      if (!product.prices || product.prices.length === 0) return false

      let hasRecurringPrice = false
      let hasNonRecurringPrice = false

      for (const price of product.prices) {
        if (
          price.recurring &&
          price.recurring.interval === selectedInterval &&
          price.recurring.interval_count === 1
        ) {
          hasRecurringPrice = true
        }
        if (!price.recurring) {
          hasNonRecurringPrice = true
        }

        if (hasRecurringPrice && hasNonRecurringPrice) break
      }

      return hasRecurringPrice || hasNonRecurringPrice
    })
    .sort((a, b) => {
      const priceA = a.prices
        ? findPriceByInterval(a.prices, selectedInterval)
        : null
      const priceB = b.prices
        ? findPriceByInterval(b.prices, selectedInterval)
        : null

      if (!priceA?.recurring && priceB?.recurring) return 1
      if (priceA?.recurring && !priceB?.recurring) return -1

      const unitAmountA = priceA ? getRegionalPrice(priceA).unitAmount : 0
      const unitAmountB = priceB ? getRegionalPrice(priceB).unitAmount : 0

      return unitAmountA - unitAmountB
    })
    .map((product) => {
      if (!product.prices || !product.activePlan) return product

      const hasActivePrice = product.prices.some((price) => price.activePrice)

      if (!hasActivePrice) {
        const monthlyPriceIndex = product.prices.findIndex(
          (price) =>
            price.recurring &&
            price.recurring.interval === BillingInterval.MONTH &&
            price.recurring.interval_count === 1,
        )
        const fallbackIndex = monthlyPriceIndex !== -1 ? monthlyPriceIndex : 0

        return {
          ...product,
          prices: product.prices.map((price, index) => ({
            ...price,
            activePrice: index === fallbackIndex,
          })),
        }
      }

      return product
    })

  let hasActivePlan = products.some((product) => product.activePlan)

  if (
    !hasActivePlan &&
    userSubscriptionPlan?.toLowerCase().includes("enterprise")
  ) {
    const enterpriseProductIndex = products.findIndex((product) =>
      product.name.toLowerCase().includes("enterprise"),
    )

    if (enterpriseProductIndex !== -1) {
      products[enterpriseProductIndex] = {
        ...products[enterpriseProductIndex],
        activePlan: true,
      }
      hasActivePlan = true
    }
  }

  if (!hasActivePlan && products.length > 0) {
    products[0] = {
      ...products[0],
      activePlan: true,
    }
  }

  return products
}
